'use client';
import type { CustomerStatus } from '@/modules/Customer/interface/Status';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, type FC } from 'react';
import { useSession } from 'next-auth/react';
import { ChevronRight, SettingsOutline } from '@eqi-investimentos/icons/web';
import {
  Flex,
  IconButton,
  Avatar,
  Divider,
  Button,
  useColorMode,
} from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { PATHS } from '@/config/paths';
import { monetaryButtonHeaderScreensRoutes } from '@/config/routesConfig';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import { useSendClickEvent } from '@/modules/Customer/http/client/mutations/useSendClickEvent';
import { MY_PROFILE_DRAWER_ID } from '@/modules/Customer/ui/ProfileDrawer/utils';
import { Thumb } from '@/components/elements/Thumb';
import { getLogoByTheme } from '@/utils/assets';
import { IfRender } from '@/utils/jsx';
import { MonetaryVisibilityButton } from '../MonetaryVisibilityButton';
import { OpenAccountDrawer } from '../OpenAccountDrawer';
import { RightActions } from './components/RightActions';
import { HeaderSkeleton } from './Skeleton';

export const Header: FC<HeaderProps> = ({ customer }) => {
  const { push } = useRouter();
  const pathname = usePathname();
  const t = useT();
  const { colorMode } = useColorMode();

  const { data: session } = useSession();
  const { mutate: sendClickEvent } = useSendClickEvent();

  const firstName = session?.user
    ? (session?.user?.name?.split(' ')[0] ?? '')
    : 'Usuário convidado';

  useEffect(() => {
    window.dispatchEvent(new Event('recheck-monetary-visibility'));
  }, [pathname]);

  return (
    <Flex
      alignItems="center"
      py="l"
      gap="5xl"
      w="full"
      maxW={{ base: 'full', '2xl': '86rem' }}
      px="3xl"
      mx="auto"
      h="6rem"
    >
      <Thumb
        href={PATHS.home}
        onClick={() => push(PATHS.home)}
        src={getLogoByTheme(colorMode === 'dark')}
        width={100}
        height={36}
        full
        fit="contain"
        alt="Logo escrito EQI+"
      />
      <Divider
        orientation="vertical"
        h="3.5rem"
        borderColor="transparency.mid"
      />
      {!customer ? (
        <HeaderSkeleton />
      ) : (
        <>
          <Flex
            id="box_card_profile_side_menu"
            transition="display 500ms ease-in-out"
            alignItems="center"
            gap="s"
            sx={{
              '.chakra-avatar': {
                w: '2.5rem',
                h: '2.5rem',
              },
            }}
          >
            <Avatar
              name={firstName}
              subtext={
                customer.suitability
                  ? `${t('attributes.profile')} ${customer.suitability.toLocaleLowerCase()}`
                  : ''
              }
            />
            <IfRender
              condition={monetaryButtonHeaderScreensRoutes.includes(pathname)}
            >
              <MonetaryVisibilityButton
                p="xs"
                ml="xs"
                bgColor="transparency.light"
                borderRadius="50%"
                iconProps={{ color: 'content.brand' }}
              />
            </IfRender>
          </Flex>
          <IfRender condition={!customer.isEqi}>
            <OpenAccountDrawer
              screen="business"
              handleBeforeDrawerOpen={() =>
                sendClickEvent({
                  label:
                    salesforceOpportunityConfig.sendClickEvent.openAccount.label(
                      window.location.pathname
                    ),
                })
              }
              customButton={
                <Button size="lg" rightIcon={<ChevronRight />}>
                  {t('attributes.openAccount')}
                </Button>
              }
            />
          </IfRender>
          <RightActions />
          <Divider
            orientation="vertical"
            h="3.5rem"
            borderColor="transparency.mid"
          />
          <IconButton
            aria-label="Abrir configurações"
            icon={<SettingsOutline color="content.brand" boxSize="1.5rem" />}
            variant="naked"
            p="xs"
            bgColor="transparency.light"
            borderRadius="50%"
            onClick={() => {
              if (!session || !session.user) {
                window.dispatchEvent(new Event('open-unauthenticated-modal'));
                return;
              }
              window.dispatchEvent(
                new CustomEvent(`open-chakra-modal-${MY_PROFILE_DRAWER_ID}`)
              );
            }}
          />
        </>
      )}
    </Flex>
  );
};

export type HeaderProps = {
  showLogo?: boolean;
  title?: string;
  customer?: Partial<CustomerStatus>;
};
